import { Capacitor } from '@capacitor/core';
import PrintIcon from '@mui/icons-material/Print';
import { useMemo } from 'react';
import ASTROLAB_STORE from '../assets/svg/astrolab-store.svg';
import CALENDAR from '../assets/svg/calendar.svg';
import CATEGORIES_MANAGE from '../assets/svg/categories-manage.svg';
import CHECK_ICON from '../assets/svg/check-icon.svg';
import CLOCK_FAST_ICON from '../assets/svg/clock-fast-icon.svg';
import FILE_ICON from '../assets/svg/file-icon.svg';
import FOOD_TRUCK from '../assets/svg/food-truck.svg';
import HOME_ICON from '../assets/svg/home-icon.svg';
import LOG_OUT_ICON from '../assets/svg/log-out-icon.svg';
import PREORDER_LIST from '../assets/svg/preorder-list.svg';
import PREORDER_RULES from '../assets/svg/preorder-rules.svg';
import PREORDER from '../assets/svg/preorder.svg';
import SETTING_ICON from '../assets/svg/settings-icon.svg';
import USER_MENUS from '../assets/svg/user-menus.svg';
import { LANG_TEXT } from '../constants/language';
import { ROLES } from '../consts/language';
import { useAuthContext } from '../context/AuthContext/AuthContext';
import { usePreorderEnabled } from '../hooks/usePreorderEnabled';

export interface SidebarItem {
  id: string;
  label: string;
  icon?: string | React.ComponentType<any>;
  path?: string;
  enable: boolean;
  onClick?: () => void;
  subItems?: SidebarItem[];
  isLogout?: boolean;
  isExpanded?: boolean;
  isExtract?: boolean;
}

interface UseSidebarItemsParams {
  handleLogoutButtonOnClick?: () => void;
}

interface UseSidebarItemsReturn {
  sidebarItems: SidebarItem[];
  staffSidebarItems: SidebarItem[];
  bottomItems: SidebarItem[];
}

export const useSidebarItems = ({ handleLogoutButtonOnClick }: UseSidebarItemsParams): UseSidebarItemsReturn => {
  const { userInfo } = useAuthContext();
  const { enabledNewVersion, isReady } = usePreorderEnabled();

  const isNativeApp =
    typeof Capacitor !== 'undefined' &&
    (typeof (Capacitor as any).isNativePlatform === 'function'
      ? (Capacitor as any).isNativePlatform()
      : typeof (Capacitor as any).getPlatform === 'function'
        ? (Capacitor as any).getPlatform() !== 'web'
        : !!(typeof window !== 'undefined' && (window as any).Capacitor));

  const isStaff = userInfo?.role === ROLES.STAFF;
  const isSuperAdmin = userInfo?.role === ROLES.SUPER_ADMIN;
  const isHeadquarter = userInfo?.role === ROLES.HEADQUARTER;

  const restaurants = useMemo(() => {
    const adminRestaurantId = userInfo?.restaurants?.find((restaurant: any) => restaurant.is_admin_store)?.id;
    const otherRestaurants = userInfo?.restaurants?.filter((restaurant: any) => !restaurant.is_admin_store) || [];

    return {
      adminRestaurantId,
      otherRestaurants,
    };
  }, [userInfo?.restaurants]);

  const sidebarItems: SidebarItem[] = useMemo(
    () => [
      {
        id: 'categories_management',
        label: LANG_TEXT.CATEGORIES_MANAGEMENT,
        icon: CATEGORIES_MANAGE,
        path: '/categories_management',
        enable: isSuperAdmin,
      },
      {
        id: 'user_menus',
        label: LANG_TEXT.USER_MENUS,
        icon: USER_MENUS,
        enable: isSuperAdmin && enabledNewVersion,
        subItems: [
          {
            id: 'regular_orders',
            label: LANG_TEXT.REGULAR_ORDERS,
            path: '/user_menus/regular_orders',
            enable: isSuperAdmin && enabledNewVersion,
          },
          {
            id: 'regular_pre_orders',
            label: LANG_TEXT.REGULAR_PRE_ORDERS,
            path: '/user_menus/regular_pre_orders',
            enable: isSuperAdmin && enabledNewVersion,
          },
          {
            id: 'special_orders',
            label: LANG_TEXT.SPECIAL_ORDERS,
            path: '/user_menus/special_orders',
            enable: isSuperAdmin && enabledNewVersion,
          },
          {
            id: 'special_pre_orders',
            label: LANG_TEXT.SPECIAL_PRE_ORDERS,
            path: '/user_menus/special_pre_orders',
            enable: isSuperAdmin && enabledNewVersion,
          },
        ],
      },
      {
        id: 'food_trucks',
        label: LANG_TEXT.FOOD_TRUCKS,
        icon: FOOD_TRUCK,
        enable: isSuperAdmin && enabledNewVersion,
        isExtract: true,
        subItems:
          restaurants.otherRestaurants.map((restaurant: any) => ({
            id: `food_trucks_${restaurant.id}`,
            label: restaurant.name,
            path: `/food_trucks/${restaurant.id}`,
            enable: isSuperAdmin && enabledNewVersion,
          })) || [],
      },
      {
        id: 'astrolab_store',
        label: LANG_TEXT.ASTROLAB_STORE,
        icon: ASTROLAB_STORE,
        path: `/astrolab-store/${restaurants.adminRestaurantId}`,
        enable: isSuperAdmin && enabledNewVersion,
      },
      {
        id: 'pre_order_rules',
        label: LANG_TEXT.PRE_ORDER_RULES,
        icon: PREORDER_RULES,
        path: '/pre_order_rules',
        enable: isSuperAdmin && enabledNewVersion,
      },
      {
        id: 'game_schedule',
        label: LANG_TEXT.GAME_SCHEDULE,
        icon: CALENDAR,
        path: '/game_schedule',
        isExtract: true,
        enable: isSuperAdmin,
      },
      {
        id: 'pre_orders',
        label: LANG_TEXT.PRE_ORDERS,
        icon: PREORDER,
        path: '/pre_orders',
        isExtract: true,
        enable: isSuperAdmin && enabledNewVersion,
        subItems: [
          {
            id: 'pre_orders_regular',
            label: LANG_TEXT.REGULAR_PRE_ORDERS,
            path: '/pre_orders/regular',
            enable: isSuperAdmin && enabledNewVersion,
          },
          {
            id: 'pre_orders_special',
            label: LANG_TEXT.SPECIAL_PRE_ORDERS,
            path: '/pre_orders/special',
            enable: isSuperAdmin && enabledNewVersion,
          },
        ],
      },
      {
        id: 'admin_order_management',
        label: LANG_TEXT.ORDER_MANAGEMENT,
        icon: CHECK_ICON,
        path: '/order_management',
        isExtract: true,
        enable: isSuperAdmin && enabledNewVersion,
      },
      {
        id: 'order',
        label: LANG_TEXT.ORDER_HISTORY,
        icon: CLOCK_FAST_ICON,
        path: '/order_history',
        enable: isSuperAdmin || isHeadquarter,
        isExtract: true,
        subItems: enabledNewVersion
          ? [
              {
                id: 'regular_order',
                label: LANG_TEXT.REGULAR_ORDERS,
                path: '/order_history/regular_orders',
                enable: isSuperAdmin || isHeadquarter,
              },
              {
                id: 'regular_pre_orders',
                label: LANG_TEXT.REGULAR_PRE_ORDERS,
                path: '/order_history/regular_pre_orders',
                enable: isSuperAdmin || isHeadquarter,
              },
              {
                id: 'special_order',
                label: LANG_TEXT.SPECIAL_ORDERS,
                path: '/order_history/special_orders',
                enable: isSuperAdmin || isHeadquarter,
              },
              {
                id: 'special_pre_order',
                label: LANG_TEXT.SPECIAL_PRE_ORDERS,
                path: '/order_history/special_pre_orders',
                enable: isSuperAdmin || isHeadquarter,
              },
            ]
          : [],
      },
    ],
    [isSuperAdmin, isHeadquarter, restaurants, enabledNewVersion]
  );

  const staffSidebarItems = useMemo(() => {
    return [
      // Staff Menus
      {
        id: 'dashboard',
        label: LANG_TEXT.HOME,
        icon: HOME_ICON,
        path: '/dashboard',
        enable: isStaff,
      },
      {
        id: 'order_management',
        label: LANG_TEXT.ORDER_MANAGEMENT,
        icon: CHECK_ICON,
        path: '/order_management',
        enable: isStaff,
      },
      {
        id: 'menu_management',
        label: LANG_TEXT.MENU_MANAGEMENT,
        icon: FILE_ICON,
        path: '/menu_management',
        enable: isStaff,
      },
      {
        id: 'food_truck_game_schedule',
        label: LANG_TEXT.GAME_SCHEDULE,
        icon: CALENDAR,
        path: '/game_schedule',
        enable: isStaff,
      },
      {
        id: 'pre_orders_list',
        label: LANG_TEXT.PRE_ORDER_LIST,
        icon: PREORDER_LIST,
        path: '/pre_orders',
        enable: isStaff && enabledNewVersion,
      },
      {
        id: 'order_history',
        label: LANG_TEXT.ORDER_HISTORY,
        icon: CLOCK_FAST_ICON,
        path: '/order_history',
        enable: isStaff,
      },
    ];
  }, [isStaff, enabledNewVersion]);

  const bottomItems: SidebarItem[] = useMemo(
    () => [
      {
        id: 'settings',
        label: LANG_TEXT.SETTINGS,
        icon: SETTING_ICON,
        path: '/settings',
        enable: isStaff && isReady && !enabledNewVersion,
      },
      {
        id: 'settings_printer',
        label: LANG_TEXT.PRINTER_SETTINGS,
        icon: PrintIcon,
        path: '/settings_printer',
        enable: isStaff && isNativeApp,
      },
      {
        id: 'logout',
        label: LANG_TEXT.LOG_OUT,
        icon: LOG_OUT_ICON,
        path: '/',
        enable: true,
        isLogout: true,
        onClick: handleLogoutButtonOnClick,
      },
    ],
    [handleLogoutButtonOnClick, isStaff, isNativeApp]
  );

  return { sidebarItems, staffSidebarItems, bottomItems };
};
